import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionHeader, Callout } from '../components/UI';
import AiMarkdown from '../components/AiMarkdown';
import { useScenario } from '../context/ScenarioContext';
import { api, formatApiErrorDetail } from '../lib/api';
import { Sparkles, Loader2, History as HistoryIcon, Bookmark, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const INVESTMENT_TYPES = [
  { value: 'new_hire', label: 'New teammate / hire' },
  { value: 'coaching_program', label: 'Coaching or mastermind program' },
  { value: 'ad_channel', label: 'New ad channel or campaign' },
  { value: 'sponsorship', label: 'Sponsorship / partnership' },
  { value: 'software', label: 'Software / tooling' },
  { value: 'equipment', label: 'Equipment / office' },
  { value: 'other', label: 'Other' },
];

export default function InvestmentAnalyzer() {
  const { active, scenarios, reloadScenarios, setActive } = useScenario();
  const navigate = useNavigate();
  const activeScenarios = useMemo(() => scenarios.filter((s) => !s.archived), [scenarios]);
  const [form, setForm] = useState({
    scenario_id: active?.id || '',
    investment_name: '',
    investment_type: 'new_hire',
    monthly_cost: 0,
    one_time_cost: 0,
    expected_benefit: '',
    timeline_months: 12,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [savingScenario, setSavingScenario] = useState(false);

  useEffect(() => {
    if (active?.id && !form.scenario_id) setForm((f) => ({ ...f, scenario_id: active.id }));
  }, [active, form.scenario_id]);

  const loadHistory = () => api.get('/ai/analyses', { params: { kind: 'investment', limit: 10 } }).then((r) => setHistory(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHistory(); }, []);

  const saveAsScenario = async () => {
    if (!result?.id) return;
    setSavingScenario(true);
    try {
      const { data } = await api.post('/ai/investment/save-scenario', { analysis_id: result.id });
      setResult((prev) => ({ ...prev, saved_scenario_id: data.scenario.id, saved_scenario_name: data.scenario.name }));
      await reloadScenarios();
      setActive(data.scenario.id);
      const tweaks = Object.keys(data.applied_deltas || {}).length;
      toast.success(`Saved "${data.scenario.name}" · ${tweaks} driver${tweaks === 1 ? '' : 's'} adjusted. Switched you to it.`);
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingScenario(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.investment_name.trim()) {
      toast.error('Give the investment a short name first.');
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post('/ai/investment', {
        ...form,
        monthly_cost: Number(form.monthly_cost) || 0,
        one_time_cost: Number(form.one_time_cost) || 0,
        timeline_months: Number(form.timeline_months) || 12,
      });
      setResult(data);
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="Investment Analyzer · Claude"
        title="Should we make this move?"
        description="Describe an investment you're weighing — a new hire, a coaching program, an ad channel, a sponsorship. Claude pressure-tests it against your active scenario, BHAG, recent actuals, and cash position."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8">
        <form onSubmit={submit} className="bg-white border-t-4 border-indie-evergreen p-6 space-y-4" data-testid="investment-form">
          <div>
            <label className="label-editorial">Scenario to pressure-test against</label>
            <select
              value={form.scenario_id}
              onChange={(e) => update('scenario_id', e.target.value)}
              className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
              data-testid="inv-scenario"
            >
              {activeScenarios.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-editorial">What's the investment?</label>
              <input
                value={form.investment_name}
                onChange={(e) => update('investment_name', e.target.value)}
                placeholder="Hire junior trademark attorney"
                className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
                data-testid="inv-name"
              />
            </div>
            <div>
              <label className="label-editorial">Type</label>
              <select
                value={form.investment_type}
                onChange={(e) => update('investment_type', e.target.value)}
                className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
                data-testid="inv-type"
              >
                {INVESTMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-editorial">Ongoing monthly cost</label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-indie-ink/50">$</span>
                <input
                  type="number"
                  value={form.monthly_cost}
                  onChange={(e) => update('monthly_cost', e.target.value)}
                  className="w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm num-tabular text-right"
                  data-testid="inv-monthly"
                />
              </div>
            </div>
            <div>
              <label className="label-editorial">One-time / setup cost</label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-indie-ink/50">$</span>
                <input
                  type="number"
                  value={form.one_time_cost}
                  onChange={(e) => update('one_time_cost', e.target.value)}
                  className="w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm num-tabular text-right"
                  data-testid="inv-onetime"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="label-editorial">Expected benefit</label>
            <textarea
              value={form.expected_benefit}
              onChange={(e) => update('expected_benefit', e.target.value)}
              rows={3}
              placeholder="8 additional packages/month at $2,800 avg after 3-month ramp"
              className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
              data-testid="inv-benefit"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-editorial">Evaluation window</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  value={form.timeline_months}
                  onChange={(e) => update('timeline_months', e.target.value)}
                  className="w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm num-tabular text-right"
                  data-testid="inv-months"
                />
                <span className="text-xs text-indie-ink/50 uppercase tracking-wider">months</span>
              </div>
            </div>
          </div>

          <div>
            <label className="label-editorial">Other context (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={2}
              placeholder="Candidate vetted, can start in 4 weeks."
              className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
              data-testid="inv-notes"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="inv-submit"
            className="w-full flex items-center justify-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-sm px-4 py-3 hover:bg-indie-yellow/85 disabled:opacity-60"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Claude is thinking…</> : <><Sparkles size={16} /> Run analysis</>}
          </button>
        </form>

        <div className="space-y-6">
          {submitting && (
            <Callout title="Claude is pressure-testing your idea" tone="default">
              Usually takes 15-40 seconds. Crunching your scenario, BHAG, closed months, and team size against the proposal.
            </Callout>
          )}

          {result && (
            <div className="bg-white border-t-4 border-indie-evergreen p-6" data-testid="inv-result">
              <div className="label-editorial text-indie-evergreen">Analysis · {new Date(result.created_at).toLocaleString()}</div>
              <div className="text-xl font-semibold text-indie-navy mt-1 mb-4">{result.investment_name}</div>

              {result.adjustments && Object.keys(result.adjustments.deltas || {}).length > 0 && (
                <div className="mb-5 bg-indie-cream border border-indie-evergreen/30 p-4">
                  <div className="label-editorial text-indie-evergreen">Proposed driver adjustments</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {Object.entries(result.adjustments.deltas).map(([key, delta]) => {
                      const rationale = result.adjustments.rationale?.[key];
                      const sign = delta >= 0 ? '+' : '';
                      const formatted = Math.abs(delta) < 1 ? `${(delta * 100).toFixed(2)}pp` : `$${delta.toLocaleString()}`;
                      return (
                        <li key={key} className="flex items-start gap-2">
                          <code className="font-mono text-xs bg-white px-1.5 py-0.5 text-indie-cobalt whitespace-nowrap">{key}</code>
                          <span className="text-indie-evergreen font-semibold num-tabular whitespace-nowrap">{sign}{formatted}</span>
                          {rationale && <span className="text-indie-ink/70">— {rationale}</span>}
                        </li>
                      );
                    })}
                  </ul>
                  {result.saved_scenario_id ? (
                    <button
                      onClick={() => { setActive(result.saved_scenario_id); navigate('/'); }}
                      className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-indie-evergreen hover:text-indie-navy"
                      data-testid="inv-open-saved-scenario"
                    >
                      <CheckCircle2 size={14} /> Saved as "{result.saved_scenario_name}" · Open dashboard
                    </button>
                  ) : (
                    <button
                      onClick={saveAsScenario}
                      disabled={savingScenario}
                      data-testid="inv-save-scenario"
                      className="mt-3 inline-flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-xs px-4 py-2 hover:bg-indie-yellow/85 disabled:opacity-60"
                    >
                      {savingScenario ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Bookmark size={14} /> Save as new scenario</>}
                    </button>
                  )}
                </div>
              )}

              <AiMarkdown>{result.narrative}</AiMarkdown>
            </div>
          )}

          {!submitting && !result && (
            <div className="bg-indie-cream border-t-4 border-indie-navy p-6">
              <div className="label-editorial">How this works</div>
              <ul className="mt-3 space-y-2 text-sm text-indie-ink/80 list-disc pl-5">
                <li>Fill in the form. Be specific — &quot;hire a paralegal at $4,500/mo who can handle 40% of current case admin&quot; beats &quot;new hire.&quot;</li>
                <li>Claude gets your active scenario, 12-month forecast, BHAG, last 3 closed months, team size, and cash position.</li>
                <li>You get a verdict (green/yellow/red), ROI read with payback math, timing recommendation, watch-list, and one thing to reconsider.</li>
                <li>No math crunched yet — this is a qualitative pressure-test. Run a few variations before committing.</li>
              </ul>
            </div>
          )}

          {history.length > 0 && (
            <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="inv-history">
              <div className="flex items-center gap-2 label-editorial"><HistoryIcon size={14} /> Past analyses</div>
              <div className="mt-3 space-y-2">
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => setResult(h)}
                    className="w-full text-left px-3 py-2 hover:bg-indie-cream transition-colors border-l-2 border-transparent hover:border-indie-evergreen"
                  >
                    <div className="text-sm font-medium text-indie-navy">{h.investment_name}</div>
                    <div className="text-xs text-indie-ink/60">{new Date(h.created_at).toLocaleString()} · {h.scenario_name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
